import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSetup, useUpdateSetup, type SetupDevice, type SetupStatus } from '../../hooks/useSetup.ts';
import { DevicePicker } from './DevicePicker.tsx';
import { SetupPaths } from './SetupPaths.tsx';
import { MUTED, PRIMARY_BUTTON } from './styles.ts';

/**
 * Profile → Integrations: "Which device do you ride with?" above the
 * connect cards. Saving updates ``/setup`` and the recommended paths.
 */
export function DeviceSetup() {
  const { data } = useSetup();
  if (!data) return null;
  return <DeviceSetupForm setup={data} />;
}

function sameDevices(a: readonly SetupDevice[], b: readonly SetupDevice[]): boolean {
  return a.length === b.length && a.every((d) => b.includes(d));
}

function DeviceSetupForm({ setup }: Readonly<{ setup: SetupStatus }>) {
  const { t } = useTranslation();
  const update = useUpdateSetup();
  const [devices, setDevices] = useState<SetupDevice[]>(setup.devices);
  const unchanged = sameDevices(devices, setup.devices);
  return (
    <section aria-labelledby="device-setup-title" className="flex flex-col gap-3">
      <h2 id="device-setup-title" className="text-sm font-semibold text-(--color-text-primary)">
        {t('setup.devices.profileTitle')}
      </h2>
      <DevicePicker value={devices} onChange={setDevices} />
      <button
        type="button"
        disabled={unchanged || update.isPending}
        onClick={() => update.mutate({ devices })}
        className={`self-start ${PRIMARY_BUTTON}`}
      >
        {t('setup.devices.save')}
      </button>
      {setup.paths.length > 0 && (
        <>
          <h3 className={MUTED}>{t('setup.devices.recommended')}</h3>
          <SetupPaths paths={setup.paths} />
        </>
      )}
    </section>
  );
}
